// Orchestrator — "from a bunch of files, produce a categorized + bucketed
// bundle the dashboard can render." NOT a full port of scripts/process_month.py
// (903 LOC); v1 covers the happy path:
//
//   - detect portal per file (CSV via detectPortalFromText; PDF via filename hint)
//   - run the right loader
//   - dedupe by Transaction.id
//   - apply rule-based categorization, threading the user's existing
//     category_cache when provided
//   - bucket by month (YYYY-MM)
//   - emit a dashboard_data.json-shaped bundle
//
// Still deferred: reconciliation passes, the LLM categorization tier
// (intentionally never wired — strict offline posture for the web app),
// merging into a persistent processed/<month>.json on disk.

import type { Transaction } from "./common.js";
import { monthKey } from "./common.js";
import { detectPortalFromText, loadAny } from "./loaders/index.js";
import { loadBmoChequingPdf, loadBmoMastercardPdf } from "./loaders/bmo_pdf.js";
import { loadWisePdf } from "./loaders/wise_pdf.js";
import { loadGenericCsv } from "./loaders/generic.js";
import { loadGenericPdf, hasGenericPdfLines } from "./loaders/generic_pdf.js";
import { loadPlaidJson, readPlaidRemovedIds } from "./loaders/plaid_json.js";
import { loadSimplefinJson } from "./loaders/simplefin_json.js";
import { pdfToTextLayout } from "./pdf.js";
import { categorizeAll, type CategoryCache } from "./categorize.js";
import { buildBundle, type Bundle } from "./bundle.js";
import * as log from "./log.js";

export interface ProcessFileResult {
  filename: string;
  portal: string;
  transactions: Transaction[];
  /** Empty on success, non-empty when the file couldn't be parsed. */
  error: string;
  /** Plaid transaction_ids the file marks as removed. Caller hands
   *  these to state.applyPlaidRemoved after merge so the matching rows
   *  in the bundle get flipped to deleted. Only populated for plaid_json
   *  files (the unified sync dump) and .removed.json sidecars; empty
   *  for everything else. */
  removedPlaidIds?: string[];
}

export interface ProcessResult {
  /** All transactions, deduped by id and categorized via rules + cache. */
  transactions: Transaction[];
  /** Per-file outcomes for the UI to surface (errors, transaction counts). */
  perFile: ProcessFileResult[];
  /** Union of removedPlaidIds across every perFile entry. Caller hands
   *  this to state.applyPlaidRemoved once per process run. */
  removedPlaidIds: Set<string>;
  /** Convenience: transactions grouped by YYYY-MM. */
  byMonth: Map<string, Transaction[]>;
  /** Dashboard-compatible bundle (same shape as data/dashboard_data.json). */
  bundle: Bundle;
  /** Newly-learned vendor → category mappings to merge into the cache. */
  newlyLearned: CategoryCache;
}

// Decide a file's kind by extension. JSON gets sub-classified by the
// filename prefix the desktop's Rust layer uses when it writes
// aggregator dumps to raw/<YYYY-MM>/.
function fileKind(filename: string): "csv" | "pdf" | "plaid_json" | "plaid_removed_json" | "simplefin_json" | "unknown" {
  const lower = filename.toLowerCase();
  if (lower.endsWith(".pdf")) return "pdf";
  if (lower.endsWith(".csv") || lower.endsWith(".tsv")) return "csv";
  if (lower.endsWith(".json")) {
    // Desktop writes plaid_<item>_<ts>.json + plaid_<item>_<ts>.removed.json,
    // and simplefin_<org>_<ts>.json. The .removed.json sidecar carries
    // ONLY a `removed` array (no added/modified) — it tells us which
    // previously-imported transactions Plaid has since reversed.
    if (lower.endsWith(".removed.json")) return "plaid_removed_json";
    if (lower.startsWith("plaid_") || lower.includes("/plaid_")) return "plaid_json";
    if (lower.startsWith("simplefin_") || lower.includes("/simplefin_")) return "simplefin_json";
    return "unknown";
  }
  return "unknown";
}

// Decode CSV bytes into text. Bank exports come in a variety of encodings —
// utf-8 with optional BOM is the common case; we fall back to cp1252-ish
// behaviour for non-UTF8 bytes by using TextDecoder's "windows-1252" label.
// Mirrors scripts/common.py::read_text_smart minus latin-1 last-ditch.
function decodeCsv(bytes: Uint8Array): string {
  // Strip UTF-8 BOM if present.
  let view = bytes;
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    view = bytes.subarray(3);
  }
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(view);
  } catch {
    return new TextDecoder("windows-1252").decode(view);
  }
}

async function processCsv(
  filename: string,
  bytes: Uint8Array,
): Promise<ProcessFileResult> {
  const text = decodeCsv(bytes);
  try {
    const { portal, transactions } = await loadAny(text, filename);
    log.info("loader", `CSV parsed: ${filename}`, {
      portal, transactions: transactions.length,
    });
    return { filename, portal, transactions, error: "" };
  } catch (err) {
    const portal = detectPortalFromText(text, filename);
    const msg = err instanceof Error ? err.message : String(err);
    // Mirror scripts/process_month.process(): when a portal-specific
    // loader raises mid-stream, retry with the generic loader before
    // giving up. Empty-but-clean parses (a real empty file) don't
    // trigger fallback — only exceptions do. Already-generic portals
    // skip the retry to avoid infinite loops.
    if (portal !== "generic_csv" && portal !== "unknown") {
      try {
        const fallback = await loadGenericCsv(text, filename);
        if (fallback.length > 0) {
          log.warn("loader", `CSV ${portal} failed, fell back to generic: ${filename}`, {
            portal, fallback_count: fallback.length, original_error: msg,
          });
          return {
            filename,
            portal: "generic_csv",
            transactions: fallback,
            error: "", // success via fallback — original error logged but not surfaced as failure
          };
        }
      } catch (fbErr) {
        log.error("loader", `CSV generic fallback also failed: ${filename}`, {
          fallback_error: fbErr instanceof Error ? fbErr.message : String(fbErr),
        });
      }
    }
    log.error("loader", `CSV failed: ${filename}`, { portal, error: msg });
    return { filename, portal, transactions: [], error: msg };
  }
}

// Plaid / SimpleFIN JSON loader entry. Both write byte-identical UTF-8
// (the desktop's reqwest path uses serde_json) so decode is trivial.
// Errors propagate as the per-file error string for the UI to surface.
async function processAggregatorJson(
  filename: string,
  bytes: Uint8Array,
  kind: "plaid" | "simplefin",
): Promise<ProcessFileResult> {
  const text = new TextDecoder("utf-8").decode(bytes);
  const portal = kind === "plaid" ? "plaid_json" : "simplefin_json";
  try {
    const txs = kind === "plaid"
      ? await loadPlaidJson(text, filename)
      : await loadSimplefinJson(text, filename);
    // Plaid's unified /transactions/sync dump may include a `removed`
    // array alongside `added`/`modified`. We parse it here so the
    // caller can apply removals after merging the additions.
    const removedPlaidIds =
      kind === "plaid" ? Array.from(readPlaidRemovedIds(text)) : undefined;
    log.info("loader", `JSON parsed: ${filename}`, {
      portal,
      transactions: txs.length,
      ...(removedPlaidIds && removedPlaidIds.length > 0
        ? { removed: removedPlaidIds.length }
        : {}),
    });
    return { filename, portal, transactions: txs, error: "", removedPlaidIds };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    log.error("loader", `JSON failed: ${filename}`, { portal, error: msg });
    return { filename, portal, transactions: [], error: msg };
  }
}

// Sidecar variant: `.removed.json` files contain ONLY the removed array,
// no added/modified. The desktop's Rust layer writes one alongside the
// main plaid_<item>_<ts>.json after each /transactions/sync; users
// dragging desktop folders into the web app expect us to honour them.
async function processPlaidRemovedJson(
  filename: string,
  bytes: Uint8Array,
): Promise<ProcessFileResult> {
  const text = new TextDecoder("utf-8").decode(bytes);
  try {
    const ids = Array.from(readPlaidRemovedIds(text));
    log.info("loader", `Plaid removed-sidecar parsed: ${filename}`, { removed: ids.length });
    return {
      filename,
      portal: "plaid_json",
      transactions: [],
      error: "",
      removedPlaidIds: ids,
    };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    log.error("loader", `Plaid removed-sidecar failed: ${filename}`, { error: msg });
    return { filename, portal: "plaid_json", transactions: [], error: msg };
  }
}

// Two-stage Wise PDF detection: fast filename hint, then a content sniff
// of the first ~2KB of PDF.js layout-text. Both branches short-circuit so
// non-Wise PDFs cost at most one PDF.js extraction (which we'd do anyway
// for generic-PDF fallback). Returns true only when we have high
// confidence — false routes the file through normal BMO/generic flow.
async function sniffWisePdf(bytes: Uint8Array, lowerName: string): Promise<boolean> {
  if (/wise|transferwise/.test(lowerName)) return true;
  // The "exported-activities" default filename + "Wise Payments" header
  // is the canonical signature for an unnamed Wise export. Filename
  // alone is not enough — users rename downloads — but combined with
  // the content marker it's airtight.
  if (!lowerName.endsWith(".pdf")) return false;
  try {
    const text = await pdfToTextLayout(bytes);
    return /Wise Payments|Transaction history\s*\n.*Wise/i.test(text.slice(0, 2048));
  } catch {
    return false;
  }
}

async function processPdf(
  filename: string,
  bytes: Uint8Array,
): Promise<ProcessFileResult> {
  const name = filename.toLowerCase();
  // Try generic-PDF as a fallback whenever a portal-specific parser
  // returns zero clean transactions. The TS BMO PDF parsers were tuned on
  // synthetic fixtures and don't yet match every real BMO statement
  // layout — generic regularly extracts a useful tx set from the same
  // file (filename "April 10, 2026.pdf" yields 0 from BMO-specific but
  // 25 from generic). Better to have rows with slightly weaker labels
  // than to give the user an empty month.
  const tryGenericFallback = async (): Promise<ProcessFileResult | null> => {
    if (!(await hasGenericPdfLines(bytes))) return null;
    const txs = await loadGenericPdf(bytes, filename);
    if (txs.length === 0) return null;
    log.info("loader", `PDF parsed (generic fallback): ${filename}`, {
      portal: "generic_pdf", transactions: txs.length,
    });
    return { filename, portal: "generic_pdf", transactions: txs, error: "" };
  };
  try {
    // Wise PDF detection — sniff a small slice of layout-text for the
    // unique "Wise Payments" header. Filename is unreliable since Wise
    // exports come down as "exported-activities (1).pdf" by default.
    // Done before BMO filename routing so a Wise PDF named "wise.pdf"
    // doesn't get yanked into the BMO branch by the "bmo" alternation.
    const isWise = await sniffWisePdf(bytes, name);
    if (isWise) {
      const txs = await loadWisePdf(bytes, filename);
      if (txs.length > 0) {
        log.info("loader", `PDF parsed: ${filename}`, {
          portal: "wise_pdf", transactions: txs.length,
        });
        return { filename, portal: "wise_pdf", transactions: txs, error: "" };
      }
      // Wise PDF with no parseable rows is rare — fall through to generic.
    }
    // Filename hints first — when the user labels a file, trust the label.
    if (/mastercard|[_\-]mc[_\-.]|^mc[_\-.]/.test(name)) {
      const txs = await loadBmoMastercardPdf(bytes, filename);
      if (txs.length > 0) {
        log.info("loader", `PDF parsed: ${filename}`, {
          portal: "bmo_mc_pdf", transactions: txs.length,
        });
        return { filename, portal: "bmo_mc_pdf", transactions: txs, error: "" };
      }
      const fb = await tryGenericFallback();
      if (fb) return fb;
      log.warn("loader", `BMO MC PDF empty: ${filename}`, {});
      return { filename, portal: "bmo_mc_pdf", transactions: [], error: "" };
    }
    if (/chequing|checking|[_\-]chq[_\-.]|^chq[_\-.]|bmo/.test(name)) {
      const txs = await loadBmoChequingPdf(bytes, filename);
      if (txs.length > 0) {
        log.info("loader", `PDF parsed: ${filename}`, {
          portal: "bmo_chq_pdf", transactions: txs.length,
        });
        return { filename, portal: "bmo_chq_pdf", transactions: txs, error: "" };
      }
      const fb = await tryGenericFallback();
      if (fb) return fb;
      log.warn("loader", `BMO chequing PDF empty: ${filename}`, {});
      return { filename, portal: "bmo_chq_pdf", transactions: [], error: "" };
    }
    // No filename hint — go straight to generic-PDF. The
    // hasGenericPdfLines probe walks the PDF once looking for ≥3 lines
    // matching `<date> ... <amount>`; if found, the full parser runs.
    // Probe-then-parse means a clearly-non-statement PDF (scanned image,
    // flyer) returns the friendly "no transactions found" error below
    // instead of garbled output.
    const fb = await tryGenericFallback();
    if (fb) return fb;
    const msg = `Couldn't find any transaction-shaped lines in ${filename}. If it's a scanned/image PDF, the parser can't read it — export a CSV from your bank instead.`;
    log.warn("loader", `PDF skipped: ${filename}`, { reason: "no_tx_lines" });
    return { filename, portal: "unknown", transactions: [], error: msg };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    log.error("loader", `PDF failed: ${filename}`, { error: msg });
    return { filename, portal: "unknown_pdf", transactions: [], error: msg };
  }
}

export interface InputFile {
  filename: string;
  bytes: Uint8Array;
}

// Convert a browser File to our InputFile shape.
export async function fileToInput(file: File): Promise<InputFile> {
  return { filename: file.name, bytes: new Uint8Array(await file.arrayBuffer()) };
}

export interface ProcessOptions {
  /** Existing vendor_key → category mapping (user's prior decisions). */
  cache?: CategoryCache;
  /** The categories taxonomy doc, passed through to the bundle for the dashboard. */
  categoriesDoc?: unknown;
}

export async function processFiles(
  files: InputFile[],
  options: ProcessOptions = {},
): Promise<ProcessResult> {
  const perFile: ProcessFileResult[] = [];
  for (const f of files) {
    const kind = fileKind(f.filename);
    if (kind === "csv") perFile.push(await processCsv(f.filename, f.bytes));
    else if (kind === "pdf") perFile.push(await processPdf(f.filename, f.bytes));
    else if (kind === "plaid_json") perFile.push(await processAggregatorJson(f.filename, f.bytes, "plaid"));
    else if (kind === "plaid_removed_json") perFile.push(await processPlaidRemovedJson(f.filename, f.bytes));
    else if (kind === "simplefin_json") perFile.push(await processAggregatorJson(f.filename, f.bytes, "simplefin"));
    else {
      perFile.push({
        filename: f.filename,
        portal: "unknown",
        transactions: [],
        error: `Unsupported file type (only .csv, .pdf, .json with plaid_/simplefin_ prefix recognised): ${f.filename}`,
      });
    }
  }

  // Dedupe by id, keeping the first occurrence (matches Python's process_month
  // dedup behavior — later files don't overwrite earlier-loaded txs).
  const byId = new Map<string, Transaction>();
  for (const r of perFile) {
    for (const t of r.transactions) {
      if (!byId.has(t.id)) byId.set(t.id, t);
    }
  }
  const transactions = [...byId.values()];

  // Apply rule-based categorization in place. Cache (user's manual recats
  // from prior sessions) takes precedence over rules.
  const newlyLearned = categorizeAll(transactions, options.cache ?? {});

  const byMonth = new Map<string, Transaction[]>();
  for (const t of transactions) {
    const key = monthKey(t.date);
    const arr = byMonth.get(key) ?? [];
    arr.push(t);
    byMonth.set(key, arr);
  }

  const bundle = buildBundle(
    byMonth,
    perFile
      .filter((r) => !r.error)
      .map((r) => ({ filename: r.filename, portal: r.portal })),
    options.categoriesDoc ?? null,
  );

  // Union of Plaid removed transaction_ids across every file in this run.
  // Caller hands this to state.applyPlaidRemoved after mergeIntoBundle so
  // the matching rows in the persisted bundle get flipped to deleted.
  const removedPlaidIds = new Set<string>();
  for (const r of perFile) {
    if (r.removedPlaidIds) for (const id of r.removedPlaidIds) removedPlaidIds.add(id);
  }

  return { transactions, perFile, byMonth, bundle, newlyLearned, removedPlaidIds };
}
